"use client";

import { useState, useEffect, useCallback } from "react";
import { ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function Gallery() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: galleryRef,
    isVisible: galleryVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const spaces = [{
    name: "LUXURY VILLAS",
    label: "PREMIUM RENTALS",
    meta: "OCEAN VIEW · PRIVATE POOL · CONCIERGE",
    description: "Experience unparalleled comfort in our hand-picked collection of luxury villas, designed for the discerning traveler.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/villa_showcase.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/villa_showcase.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/villa_showcase.png-wvc-sizes"
  }, {
    name: "FRACTIONAL SUITES",
    label: "OWNERSHIP SOLUTIONS",
    meta: "HIGH YIELD · FULLY MANAGED · FLEXIBLE",
    description: "Maximize your investment with our fractional ownership program, offering professional management and consistent returns.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/fractional_suite.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/fractional_suite.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/fractional_suite.webp-wvc-sizes"
  }, {
    name: "COMMERCIAL ASSETS",
    label: "INVESTMENT PORTFOLIO",
    meta: "STRATEGIC LOCATION · HIGH TRAFFIC · GROWTH",
    description: "Access exclusive commercial real estate opportunities in prime Los Cabos locations with our expert guidance.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/commercial_asset.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/commercial_asset.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/commercial_asset.png-wvc-sizes"
  }, {
    name: "PRIVATE ESTATES",
    label: "EXCLUSIVE LISTINGS",
    meta: "GATED COMMUNITY · SECURITY · PRIVACY",
    description: "Discover our portfolio of private estates, offering the ultimate in security, privacy, and architectural excellence.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/private_estate.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/private_estate.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/private_estate.png-wvc-sizes"
  }];
  const handleSlideChange = useCallback((newIndex: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setIsTransitioning(false);
    }, 300);
  }, []);
  const nextSlide = useCallback(() => {
    handleSlideChange((currentIndex + 1) % spaces.length);
  }, [currentIndex, spaces.length, handleSlideChange]);
  const prevSlide = useCallback(() => {
    handleSlideChange((currentIndex - 1 + spaces.length) % spaces.length);
  }, [currentIndex, spaces.length, handleSlideChange]);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [nextSlide, isPaused]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);
  const currentSpace = spaces[currentIndex];
  return <section data-section-id="3461" id="gallery" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div ref={headerRef} className={`bg-card/60 border-y border-border py-20 md:py-28 text-center px-6 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <p className="font-default text-xs md:text-sm tracking-[0.3em] text-primary uppercase mb-6">
          Our Portfolio
        </p>
        <h2 className="font-default font-light text-5xl md:text-7xl lg:text-[80px] leading-[1.05] tracking-tight text-foreground">
          Elite <em className="italic">Properties.</em>
        </h2>
        <div className="w-12 h-px bg-primary mx-auto mt-10" />
      </div>

      <div ref={heroRef} className={`relative w-full mt-16 md:mt-24 transition-all duration-1000 ease-out ${heroVisible ? "opacity-100" : "opacity-0"}`}>
        <div className="relative w-full h-[70vh] min-h-[420px] overflow-hidden">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/hero_property.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/hero_property.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/hero_property.png-wvc-sizes" alt="Luxury property exterior with ocean view" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/10 to-background/30" />

          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 bg-card border border-border p-6 md:p-8 max-w-sm shadow-lg">
            <p className="font-default text-[10px] md:text-xs tracking-[0.25em] text-muted-foreground uppercase mb-3">
              01 · Featured Listing
            </p>
            <h3 className="font-default text-base md:text-lg tracking-[0.18em] text-foreground uppercase mb-4">
              Cabo San Lucas Estate
            </h3>
            <p className="font-default text-[10px] md:text-[11px] tracking-[0.15em] text-primary uppercase leading-relaxed">
              5 Bedrooms · Private Beach Access · Full Service
            </p>
          </div>
        </div>
      </div>

      <div ref={galleryRef} className={`mt-20 md:mt-28 transition-all duration-1000 ease-out ${galleryVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="px-6 md:px-12 mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="font-default text-xs tracking-[0.3em] text-primary uppercase mb-3">
              Management Services
            </p>
            <h3 className="font-default font-light text-3xl md:text-4xl text-foreground">
              Excellence in every detail.
            </h3>
          </div>
          <div className="flex items-center gap-6">
            <span className="font-mono text-xs tracking-widest text-muted-foreground">
              {String(currentIndex + 1).padStart(2, '0')} / {String(spaces.length).padStart(2, '0')}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={prevSlide} className="rounded-lg border-border hover:bg-accent" aria-label="Previous slide">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={nextSlide} className="rounded-lg border-border hover:bg-accent" aria-label="Next slide">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="relative px-6 md:px-12 flex flex-col" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <div className="relative overflow-hidden min-h-[420px] sm:min-h-[480px] md:min-h-[600px]">
            <div data-index={currentIndex} className={cn("w-full transition-opacity duration-600 ease-in-out flex flex-col md:flex-row gap-8 md:gap-12", isTransitioning ? "opacity-0" : "opacity-100")}>
              <div className="md:w-2/3">
                <Button onClick={() => setOpenIndex(currentIndex)} variant="ghost" className="group relative w-full h-[300px] sm:h-[400px] md:h-[600px] p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background overflow-hidden rounded-lg">
                  <div className="relative w-full h-full overflow-hidden bg-card">
                    <img src={currentSpace.src} data-wvc-srcset={currentSpace.srcset} data-wvc-sizes={currentSpace.sizes} alt={currentSpace.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-background/20 group-hover:bg-background/10 transition-colors duration-500" />
                    <div className="absolute top-6 left-6">
                      <span className="font-default text-xs tracking-[0.25em] text-foreground bg-background/80 backdrop-blur-sm px-3 py-1 uppercase rounded-sm">
                        0{currentIndex + 2}
                      </span>
                    </div>
                  </div>
                </Button>
              </div>

              <div className="md:w-1/3 flex flex-col justify-center">
                <p className="font-default text-xs md:text-sm tracking-[0.22em] text-primary uppercase mb-4">
                  {currentSpace.label}
                </p>
                <h4 className="font-default text-2xl md:text-3xl tracking-[0.1em] text-foreground uppercase mb-6">
                  {currentSpace.name}
                </h4>
                <p className="font-default text-sm text-muted-foreground leading-relaxed mb-4 max-w-md">
                  {currentSpace.description}
                </p>
                <p className="font-default text-[10px] tracking-[0.15em] text-primary uppercase mb-8">
                  {currentSpace.meta}
                </p>
                <Button onClick={() => setOpenIndex(currentIndex)} variant="outline" className="w-fit rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all">
                  View Details
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-border mt-12 relative overflow-hidden">
            <div className="absolute top-0 left-0 h-full bg-primary transition-all duration-700 ease-in-out" style={{
            width: `${(currentIndex + 1) / spaces.length * 100}%`
          }} />
          </div>

          <div className="flex justify-center gap-3 mt-10">
            {spaces.map((_, i) => <Button key={i} variant="ghost" size="icon" onClick={() => handleSlideChange(i)} className={cn("w-2 h-2 p-0 rounded-lg transition-all duration-300", i === currentIndex ? "bg-primary w-8" : "bg-border hover:bg-primary/50")} aria-label={`Go to slide ${i + 1}`} />)}
          </div>
        </div>
      </div>

      <div ref={footerRef} className={`mt-24 md:mt-32 px-6 text-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="w-px h-12 bg-border mx-auto mb-8" />
        <p className="font-default text-[11px] md:text-xs tracking-[0.3em] text-muted-foreground uppercase mb-6">
          Consultations Available By Appointment
        </p>
        <Link to="/ContactPage" className="inline-flex items-center font-default text-xs md:text-sm tracking-[0.25em] text-primary uppercase underline underline-offset-8 hover:text-primary/70 transition-colors">
          Book a consultation
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </div>

      <Dialog open={openIndex !== null} onOpenChange={open => !open && setOpenIndex(null)}>
        <DialogContent showCloseButton={false} className="max-w-6xl w-[95vw] bg-background border border-border p-0 overflow-hidden rounded-lg">
          {openIndex !== null && <div className="grid md:grid-cols-[1fr_360px]">
              <div className="relative bg-card">
                <img src={spaces[openIndex].src} data-wvc-srcset={spaces[openIndex].srcset} data-wvc-sizes={spaces[openIndex].sizes} alt={spaces[openIndex].name} loading="lazy" className="w-full h-[50vh] md:h-[80vh] object-cover" />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-between bg-card">
                <div>
                  <div className="flex items-start justify-between mb-8">
                    <span className="font-default text-[10px] tracking-[0.25em] text-primary uppercase">
                      0{openIndex + 2} · Service
                    </span>
                    <Button onClick={() => setOpenIndex(null)} variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground transition-colors rounded-lg" aria-label="Close">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <DialogTitle className="font-default text-lg md:text-xl tracking-[0.22em] text-foreground uppercase mb-4">
                    {spaces[openIndex].name}
                  </DialogTitle>
                  <p className="font-default text-[10px] tracking-[0.2em] text-primary uppercase mb-8">
                    {spaces[openIndex].label}
                  </p>
                  <div className="w-8 h-px bg-border mb-6" />
                  <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed mb-8">
                    {spaces[openIndex].description}
                  </p>
                  <p className="font-default text-[10px] tracking-[0.18em] text-foreground/80 uppercase leading-relaxed">
                    {spaces[openIndex].meta}
                  </p>
                </div>
                <div className="mt-10 pt-8 border-t border-border">
                  <p className="font-default text-[10px] tracking-[0.2em] text-muted-foreground uppercase">
                    Professional Management · Los Cabos
                  </p>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}